import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Button, Card, Form } from "react-bootstrap";
import { Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form as IKForm } from "formik";
import { SingleValue } from "react-select";
import QRScanner from "../components/QRScanner";
import ScannerField from "../components/utilities/ScannerField";
import Asset from "../components/asset";
import ApiClient from "../utilities/http";
import { Endpoints } from "../utilities/endpoints";
import { IAsset } from "../models/IAsset";
import { Option } from "../models/types";
import { toastError, toastSuccess } from "../actions/formInputAction";

interface Props {
    dispatch: Function;
}

interface AssetStatusOption {
    id: string;
    name: string;
}

interface FormValues {
    location_id: SingleValue<Option>;
}

const decodeAssetScan = (data: string): string | null => {
    try {
        const url = new URL(data);
        return url.searchParams.get("asset");
    } catch (_err) {
        return data;
    }
};

const ModifyAsset: React.FC<Props> = ({ dispatch }) => {
    const nav = useNavigate();
    const { i18n } = useTranslation();
    // @ts-ignore
    const t = (txt: string) => i18n.t(txt);

    const [asset, setAsset] = useState<IAsset | undefined>(undefined);
    const [statuses, setStatuses] = useState<AssetStatusOption[]>([]);
    const [selectedStatusId, setSelectedStatusId] = useState<string>("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        ApiClient.get(Endpoints.ASSET_STATUSES)
            .then((response) => {
                setStatuses(response.data?.statuses || []);
            })
            .catch((error) => {
                dispatch(toastError(error, "Error", "Unable to fetch statuses"));
            });
    }, [dispatch]);

    const fetchAsset = async (rawData: string) => {
        const scannedValue = decodeAssetScan(rawData);
        if (!scannedValue) {
            return;
        }

        try {
            const response = await ApiClient.get(`${Endpoints.ASSET}?code=${encodeURIComponent(scannedValue)}`);
            const nextAsset: IAsset | undefined = response.data?.asset_item;
            if (!nextAsset) {
                dispatch(toastError({}, "Unknown Asset", "The scan code does not resolve to a known asset"));
                return;
            }

            setAsset(nextAsset);
            setSelectedStatusId(String((nextAsset as any)?.status?.id || ""));
        } catch (error) {
            dispatch(toastError(error, "Error", "Unable to fetch asset details"));
        }
    };

    const initialValues = useMemo<FormValues>(() => {
        if (!asset?.location) {
            return { location_id: null };
        }

        return {
            location_id: {
                value: asset.location.id,
                label: asset.location.name,
            },
        };
    }, [asset]);

    const submit = async (values: FormValues) => {
        if (!asset?.id) {
            dispatch(toastError({}, "No Asset", "Scan or search for an asset first"));
            return;
        }

        const locationId = values.location_id?.value;
        if (!locationId || !selectedStatusId) {
            dispatch(toastError({}, "Missing Fields", "Status and location are required"));
            return;
        }

        setIsSaving(true);

        try {
            await ApiClient.post(`${Endpoints.ASSET}/${asset.id}/details`, {
                status_id: selectedStatusId,
                location_id: locationId,
            });

            await fetchAsset(asset.id);

            dispatch(toastSuccess(t("success"), "Asset updated successfully"));
            setAsset(undefined);
        } catch (error) {
            dispatch(toastError(error, "Update Failed", "Unable to update asset"));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div style={{ marginBottom: -10 }}>
                <Home style={{ marginTop: "-10px", color: "#0e6efd" }} onClick={() => nav("/dashboard")} />
                <span style={{ marginLeft: 5, fontSize: 20 }}>≫</span>
                <span
                    style={{
                        marginBottom: "10px",
                        marginRight: "10px",
                        fontSize: "18px",
                        fontWeight: "bolder",
                        padding: "5px",
                    }}
                >
                    Modify
                </span>
            </div>

            <QRScanner onResult={fetchAsset} scanOnload={false} searchController="asset" />

            {!asset && (
                <Card className="mt-3">
                    <Card.Body>{t("scan") || "Scan"} / {t("search") || "Search"} asset to modify.</Card.Body>
                </Card>
            )}

            {asset && (
                <>
                    <Asset asset={asset} showControls={false} />

                    <Card style={{marginTop: '-150px'}}>
                        <Card.Body>
                            <Formik initialValues={initialValues} enableReinitialize onSubmit={submit}>
                                {() => (
                                    <IKForm>
                                        <Form.Group controlId="status_id" className="mb-3">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select
                                                value={selectedStatusId}
                                                onChange={(event) => setSelectedStatusId(event.target.value)}
                                            >
                                                <option value="">Select status</option>
                                                {statuses.map((status) => (
                                                    <option key={status.id} value={status.id}>
                                                        {status.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <ScannerField
                                            scanOnload={false}
                                            label={t("location")}
                                            name="location_id"
                                            entity={asset.location}
                                        />

                                        <Button
                                            type="submit"
                                            className="mt-3"
                                            disabled={isSaving}
                                            style={{ width: "100%" }}
                                        >
                                            {isSaving ? "Saving..." : "Update Asset"}
                                        </Button>
                                    </IKForm>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                </>
            )}
        </>
    );
};

export default connect()(ModifyAsset);
